//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { ReactNode } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface StepWithTitle {
  title?: ReactNode;
}

interface CustomStepIndicatorProps {
  steps: StepWithTitle[];
  currentStep: number;
  goToStep?: (index: number) => void;
  disableNavigation?: boolean;
}

/**
 * Custom step indicator component with tooltips for tour navigation.
 */
const CustomStepIndicator = ({
  steps,
  currentStep,
  goToStep = () => {},
  disableNavigation = false
}: CustomStepIndicatorProps) => {
  const theme = useTheme();

  return (
    <Box
      sx = {{
        display: 'flex',
        justifyContent: 'center',
        margin: [2, 0],
        padding: 0.5,
      }}
    >
      {steps.map((step, index) => {
        return (
          <Tooltip
            key={index}
            title={step.title || `Step ${index + 1}`}
            placement="top"
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                  padding: '6px 10px',
                  fontWeight: 500,
                  fontSize: '0.8rem'
                }
              },
              popper: {
                sx: {
                  zIndex: 2200 // Ensure popper is above everything.
                }
              }
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => !disableNavigation && goToStep(index)}
              aria-label={
                typeof step.title === 'string' ?
                  `Go to step ${index + 1}: ${step.title}` :
                  `Go to step ${index + 1}`
              }
              aria-current={currentStep === index ? 'step' : undefined}
              aria-disabled={disableNavigation || undefined}
              sx={{
                // Reset native button styling so the dot looks like a dot.
                appearance: 'none',
                border: 'none',
                padding: 0,
                display: 'inline-block',
                cursor: disableNavigation ? 'default' : 'pointer',
                opacity: disableNavigation && currentStep !== index ? 0.5 : 1,
                width: 12,
                height: 12,
                margin: '0 4px',
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                backgroundColor: currentStep === index ?
                                 theme.colors.rocketRed.p50 :
                                 theme.colors.grays.astral4,
                '&:focus-visible': {
                  outline: `2px solid ${theme.colors.rocketRed.p50}`,
                  outlineOffset: '2px',
                },
                ...(disableNavigation ? {} :
                    {
                      '&:hover': {
                        transform: 'scale(1.2)',
                        backgroundColor: currentStep === index ?
                                         theme.colors.rocketRed.p60 :
                                         theme.colors.grays.astral3,
                      },
                    }),
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default CustomStepIndicator;
